import type { AskDraft } from '@/api/hooks/askSchemas';

export type AskOverlayPhase =
  | 'entry' | 'typing' | 'parsing' | 'cancelled' | 'no-match' | 'unsupported'
  | 'needs-narrower-scope' | 'partial-source' | 'likely-related-review' | 'preview-ready'
  | 'confirmation' | 'committing' | 'checking-result' | 'expired' | 'changed-since'
  | 'failed' | 'committed';

export type AskOverlayState = {
  phase: AskOverlayPhase;
  sentence: string;
  draft: AskDraft | null;
  acceptedIds: string[];
  exactIds: string[];
  requestId: number;
  message: string | null;
};

export const initialAskOverlayState: AskOverlayState = {
  phase: 'entry',
  sentence: '',
  draft: null,
  acceptedIds: [],
  exactIds: [],
  requestId: 0,
  message: null,
};

export type AskOverlayAction =
  | { type: 'TYPE'; sentence: string }
  | { type: 'PREVIEW_REQUEST'; requestId: number; sentence?: string }
  | { type: 'PREVIEW_CANCEL'; requestId: number }
  | { type: 'PREVIEW_SUCCESS'; requestId: number; draft: AskDraft }
  | { type: 'PREVIEW_FAILED'; requestId: number; message: string }
  | { type: 'TOGGLE_ID'; id: string }
  | { type: 'BEGIN_CONFIRMATION' }
  | { type: 'COMMIT_REQUEST' }
  | { type: 'COMMIT_SUCCESS' }
  | { type: 'COMMIT_UNKNOWN' }
  | { type: 'COMMIT_CONFLICT'; message: string }
  | { type: 'COMMIT_EXPIRED'; message: string }
  | { type: 'COMMIT_FAILED'; message: string }
  | { type: 'RESET' };

function phaseForDraft(draft: AskDraft): AskOverlayPhase {
  if (draft.state === 'no_match') return 'no-match';
  if (draft.state === 'unsupported') return 'unsupported';
  if (draft.state === 'needs_narrower_scope') return 'needs-narrower-scope';
  if (draft.state === 'partial_source' || draft.coverage.state === 'partial_source') return 'partial-source';
  if (draft.state === 'expired') return 'expired';
  if (draft.state === 'changed_since') return 'changed-since';
  if (draft.state === 'failed') return 'failed';
  if (draft.state === 'committed') return 'committed';
  if (draft.review_rows.some((row) => row.review_bucket === 'likely_related')) return 'likely-related-review';
  return 'preview-ready';
}

function selectedExactIds(draft: AskDraft): string[] {
  return draft.review_rows.filter((row) => row.review_bucket === 'exact' && row.bank_state !== 'non_writable').map((row) => row.line_item_id);
}

export function askOverlayReducer(state: AskOverlayState, action: AskOverlayAction): AskOverlayState {
  switch (action.type) {
    case 'TYPE':
      return { ...state, phase: action.sentence.trim() ? 'typing' : 'entry', sentence: action.sentence, message: null };
    case 'PREVIEW_REQUEST':
      return { ...state, phase: 'parsing', requestId: action.requestId, sentence: action.sentence ?? state.sentence, message: null };
    case 'PREVIEW_CANCEL':
      return action.requestId === state.requestId ? { ...state, phase: 'cancelled', draft: null, acceptedIds: [], exactIds: [] } : state;
    case 'PREVIEW_SUCCESS':
      if (action.requestId !== state.requestId || state.phase === 'cancelled') return state;
      return {
        ...state,
        phase: phaseForDraft(action.draft),
        sentence: action.draft.sentence,
        draft: action.draft,
        exactIds: selectedExactIds(action.draft),
        acceptedIds: selectedExactIds(action.draft),
        message: action.draft.message,
      };
    case 'PREVIEW_FAILED':
      return action.requestId === state.requestId ? { ...state, phase: 'failed', draft: null, acceptedIds: [], exactIds: [], message: action.message } : state;
    case 'TOGGLE_ID':
      if (!['likely-related-review', 'preview-ready', 'confirmation'].includes(state.phase)) return state;
      return { ...state, acceptedIds: state.acceptedIds.includes(action.id) ? state.acceptedIds.filter((id) => id !== action.id) : [...state.acceptedIds, action.id] };
    case 'BEGIN_CONFIRMATION':
      return state.draft && (state.acceptedIds.length > 0 || state.draft.supported_outcomes.includes('condition')) ? { ...state, phase: 'confirmation' } : state;
    case 'COMMIT_REQUEST':
      return state.draft && (state.acceptedIds.length > 0 || state.draft.supported_outcomes.includes('condition')) ? { ...state, phase: 'committing' } : state;
    case 'COMMIT_SUCCESS':
      return { ...state, phase: 'committed', message: null };
    case 'COMMIT_UNKNOWN':
      return { ...state, phase: 'checking-result' };
    case 'COMMIT_CONFLICT':
      return { ...state, phase: 'changed-since', message: action.message };
    case 'COMMIT_EXPIRED':
      return { ...state, phase: 'expired', message: action.message };
    case 'COMMIT_FAILED':
      return { ...state, phase: 'failed', message: action.message };
    case 'RESET':
      return { ...initialAskOverlayState, sentence: state.sentence };
    default:
      return state;
  }
}
